import { useCallback } from "react"
import { useAsync } from "react-use"
import { createContainer } from "unstated-next"

import { Account } from "../module/account"
import { getClaimableRewards, listMakerAprs, listMarketAprs } from "../service/liquidityMining"

export const LiquidityMiningServiceContainer = createContainer(useLiquidityMiningService)

function useLiquidityMiningService() {
    const { address } = Account.useContainer()

    const {
        value: claimableRewardsResponse,
        loading: isGetClaimableRewardsLoading,
    } = useAsync(async () => {
        if (!address) {
            return null
        }
        return getClaimableRewards(address)
    }, [address])

    const {
        value: makerMarkets,
        loading: isListMakerLoading,
    } = useAsync(async () => {
        if (!address) {
            return null
        }
        return listMakerAprs(address)
    }, [address])

    const {
        value: listMarketRes,
    } = useAsync(async () => listMarketAprs(), [])

    const makerMarketSymbols = makerMarkets?.map(market => market.marketSymbol) || []
    const restMarkets = listMarketRes?.items.filter(({ marketSymbol }) => !makerMarketSymbols.includes(marketSymbol))

    const getMakerMarket = useCallback(
        (base: string, quote: string) => {
            return makerMarkets?.find(
                ({ marketSymbol }) => marketSymbol === `v${base.toUpperCase()}/v${quote.toUpperCase()}`,
            )
        },
        [makerMarkets],
    )
    const getDefaultMarket = useCallback(
        (base: string, quote: string) => {
            return restMarkets?.find(
                ({ marketSymbol }) => marketSymbol === `v${base.toUpperCase()}/v${quote.toUpperCase()}`,
            )
        },
        [restMarkets],
    )
    const totalNetValue = makerMarkets?.reduce((acc, market) => acc + market.netValue, 0)
    const totalEstimatedReward = makerMarkets?.reduce((acc, market) => acc + market.estimatedRewardAmount, 0)
    const totalEstimatedRewardUsd = makerMarkets?.reduce((acc, market) => acc + market.estimatedRewardUsd, 0)
    const totalEstimatedRewardOp = makerMarkets?.reduce((acc, market) => acc + market.estimatedRewardOpAmount, 0)
    const totalEstimatedRewardUsdOp = makerMarkets?.reduce((acc, market) => acc + market.estimatedRewardOpUsd, 0)

    return {
        makerMarkets,
        restMarkets,
        getDefaultMarket,
        getMakerMarket,
        isLoading: isListMakerLoading || isGetClaimableRewardsLoading,
        totalNetValue,
        totalEstimatedReward,
        totalEstimatedRewardUsd,
        totalClaimableReward: claimableRewardsResponse?.totalBalance,
        totalClaimableRewardUsd: claimableRewardsResponse?.totalBalanceUsd,
        claimableRewards: claimableRewardsResponse?.items,
        totalEstimatedRewardOp,
        totalEstimatedRewardUsdOp,
        totalClaimableRewardOp: claimableRewardsResponse?.totalBalanceOp,
        totalClaimableRewardUsdOp: claimableRewardsResponse?.totalBalanceUsdOp,
        claimableRewardsOp: claimableRewardsResponse?.itemsOp,
    }
}
